<template>
	<!-- Laid out like Edit Video (ClipVideoDialog): full screen, the media on the left, the list on the right. -->
	<v-dialog
		v-model="visible"
		fullscreen
		persistent
		content-class="snapshot-measure-fullscreen-dialog"
		transition="dialog-bottom-transition"
		@keydown.esc="visible = false">
		<v-card v-test="'snapshot-measure-dialog'" class="h-100 d-flex flex-column">
			<v-card-title class="d-flex align-center flex-shrink-0" :class="smAndDown ? 'pa-2 px-3' : 'pa-4'">
				<h5 v-if="!smAndDown" class="text-h5">Measure</h5>
				<h6 v-else class="text-h6">Measure</h6>
				<v-spacer />
				<v-btn
					v-test="'snapshot-measure-done'"
					color="primary"
					variant="flat"
					rounded="xl"
					:size="smAndDown ? 'small' : 'default'"
					@click="visible = false">
					Done
				</v-btn>
			</v-card-title>

			<v-card-text class="d-flex flex-column flex-grow-1 overflow-hidden" :class="smAndDown ? 'pa-2' : 'pa-4'">
				<div class="snapshot-measure-body d-flex" :class="mdAndDown ? 'flex-column ga-3 overflow-y-auto' : 'flex-row ga-4'">
					<div class="snapshot-measure-photo rounded-lg" :class="{ 'snapshot-measure-photo--stacked': mdAndDown }">
						<ZoomablePhoto :src="src" :lazy-src="lazySrc" :alt="alt || 'Photo to measure'">
							<template #image-overlay="{ naturalWidth, naturalHeight }">
								<SnapshotMeasureLayer :measure="measure" :natural-width="naturalWidth" :natural-height="naturalHeight" />
							</template>
						</ZoomablePhoto>
					</div>

					<div
						class="snapshot-measure-side d-flex flex-column flex-shrink-0"
						:class="{ 'snapshot-measure-side--stacked': mdAndDown }">
						<div class="d-flex justify-space-between align-center mb-3 flex-shrink-0">
							<h6 class="text-h6">Measurements</h6>
							<span class="text-body-2 text-medium-emphasis">{{ measurements.length }}</span>
						</div>

						<v-alert
							v-if="notice"
							v-test="'snapshot-measure-notice'"
							class="mb-3 flex-shrink-0"
							:type="notice.kind === 'error' ? 'error' : 'info'"
							variant="tonal"
							density="compact"
							closable
							@click:close="dismissNotice">
							{{ notice.message }}
						</v-alert>

						<div v-if="!measurements.length && !pendingTap" class="text-body-2 text-medium-emphasis pb-3">
							Click an object in the photo to measure it. Each measurement shows up here.
						</div>

						<v-list class="snapshot-measure-list flex-grow-1 pa-0 bg-transparent overflow-y-auto">
							<v-list-item
								v-for="(measurement, index) in measurements"
								:key="measurement.id"
								v-test="'snapshot-measure-item'"
								class="mb-2"
								rounded="lg"
								:active="measurement.id === selectedId"
								@click="select(measurement.id)">
								<template #prepend>
									<v-avatar
										size="24"
										variant="flat"
										:color="measurement.id === selectedId ? 'primary' : 'grey-darken-1'"
										class="mr-3 text-caption font-weight-bold">
										{{ index + 1 }}
									</v-avatar>
								</template>
								<v-list-item-title v-test="'snapshot-measure-size'" class="text-body-2 font-weight-bold text-wrap">
									<span
										v-if="pendingTap?.targetId === measurement.id"
										class="d-inline-flex align-center ga-2 font-weight-regular">
										<v-progress-circular indeterminate size="14" width="2" color="primary" />
										Measuring again…
									</span>
									<template v-else>{{ sizeFor(measurement) }}</template>
								</v-list-item-title>
								<v-btn
									v-test="'snapshot-measure-try-another'"
									class="px-1 mt-1"
									color="primary"
									variant="text"
									size="small"
									:disabled="!!pendingTap"
									@click.stop="remeasure(measurement.id)">
									Try another reading
								</v-btn>
								<div v-if="warningFor(measurement)" class="text-caption text-error mt-1">{{ warningFor(measurement) }}</div>
								<template #append>
									<v-btn
										v-test="'snapshot-measure-remove'"
										icon
										variant="plain"
										color="error"
										density="compact"
										aria-label="Remove measurement"
										@click.stop="remove(measurement.id)">
										<v-icon>icon-trash-full-stroke</v-icon>
										<v-tooltip activator="parent" location="top">Remove measurement</v-tooltip>
									</v-btn>
								</template>
							</v-list-item>
							<v-list-item
								v-if="pendingTap && pendingTap.targetId === null"
								v-test="'snapshot-measure-pending'"
								rounded="lg"
								class="mb-2">
								<template #prepend>
									<v-progress-circular class="mr-3" indeterminate size="20" width="2" color="primary" />
								</template>
								<v-list-item-title class="text-body-2">Measuring…</v-list-item-title>
							</v-list-item>
						</v-list>

						<v-divider class="mt-4" />
						<div class="flex-shrink-0 pt-4">
							<div class="text-subtitle-2 text-medium-emphasis mb-2">How to measure?</div>
							<ol class="pl-6">
								<li class="text-caption text-medium-emphasis mb-1">Click an object in the photo to measure it.</li>
								<li class="text-caption text-medium-emphasis mb-1">Zoom in with the + button, and drag to move around.</li>
								<li class="text-caption text-medium-emphasis mb-1">
									If the outline doesn't match the object, click it again or use Try another reading to re-measure it.
								</li>
								<li class="text-caption text-medium-emphasis">Measurements are kept until you close this photo.</li>
							</ol>
						</div>
					</div>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
	import { useDisplay } from "vuetify";

	import SnapshotMeasureLayer from "@/components/measure/SnapshotMeasureLayer.vue";
	import ZoomablePhoto from "@common-ui/media/ZoomablePhoto.vue";
	import {
		formatMeasurementSize,
		measurementWarning,
		NOTHING_MEASURABLE_MESSAGE,
		type SnapshotMeasure,
		type SnapshotMeasurement
	} from "@/composables/useSnapshotMeasure";

	const props = defineProps<{ measure: SnapshotMeasure; src: string; lazySrc?: string; alt?: string }>();
	const visible = defineModel<boolean>("visible", { required: true });
	const { smAndDown, mdAndDown } = useDisplay();
	const { measurements, pendingTap, notice, selectedId, select, remove, remeasure, dismissNotice } = props.measure;

	function sizeFor(measurement: SnapshotMeasurement): string {
		const reading = measurement.readings[0];
		return (reading && formatMeasurementSize(reading)) || NOTHING_MEASURABLE_MESSAGE;
	}

	function warningFor(measurement: SnapshotMeasurement): string | null {
		return measurementWarning(measurement.response, measurement.readings[0] ?? null);
	}
</script>

<style scoped>
	.snapshot-measure-body {
		flex: 1;
		overflow: hidden;
	}
	.snapshot-measure-photo {
		flex: 1 1 auto;
		min-width: 0;
		display: flex;
		overflow: hidden;
		background-color: #000;
	}
	.snapshot-measure-photo--stacked {
		flex: none;
		height: 55vh;
	}
	.snapshot-measure-side {
		width: 360px;
		min-width: 320px;
		overflow: hidden;
	}
	.snapshot-measure-side--stacked {
		width: 100%;
		min-width: 0;
		overflow: visible;
	}
	.snapshot-measure-list {
		min-height: 0;
	}
</style>
